import {
  ApplicationConfig,
  provideAppInitializer,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
  inject,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { StoreModule } from '@ngrx/store';
import { effects, metaReducers, reducers } from './reducers';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AuthenticatorService } from './authentication/service/authenticatorService';
import { TenantService } from './authentication/service/tenantService';
import { TokenInterceptor } from './authentication/service/tokenInterceptor';
import { PhoneNumberPipe } from './shared/pipes/phone-number.pipe';
import { BooleanYesNoPipe } from './shared/pipes/boolean-yes-no.pipe';
import { FormQuestionTypePipe } from './shared/pipes/form-question-type.pipe';
import { SupplierStatusTagSeverityPipe } from './shared/pipes/supplier-status-tag-severity.pipe';
import { PermissionsDirective } from './shared/directive/permissions.directive';
import { MessageService } from 'primeng/api';
import { AuthGuard } from './authentication/guard/auth.guard';
import { RoleGuard } from './authentication/guard/role-guard.service';
import { routes } from './app.routes';
import { ApiModule } from './api/api.module';
import { environment } from '../../environments';
import { DatePipe } from '@angular/common';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { SLMPreset } from './primeng-preset';
import { LoadingStateService } from './shared/services/loading-state.service';
import { LoadingStateInterceptor } from './shared/interceptors/loading-state.interceptor';
import { TelemetryService } from './shared/services/telemetry/telemetry.service';

export const appConfig: ApplicationConfig = {
  providers: [
    //  Core Angular
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' })),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),

    // PrimeNG Theme
    providePrimeNG({
      theme: {
        preset: SLMPreset,
        options: { darkModeSelector: 'none' },
      },
      ripple: true,
      inputVariant: 'filled',
      zIndex: {
        modal: 2200,
        overlay: 2100,
        menu: 2100,
        tooltip: 2300,
      },
    }),

    // Imports
    importProvidersFrom(
      ApiModule.forRoot({ rootUrl: environment.apiUrl }),
      StoreModule.forRoot(reducers, { metaReducers }),
      EffectsModule.forRoot(effects),
      StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() })
    ),

    // Guards & Interceptors
    AuthGuard,
    RoleGuard,
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },

    { provide: HTTP_INTERCEPTORS, useClass: LoadingStateInterceptor, multi: true },

    provideAppInitializer(() => inject(LoadingStateService).init()),
    provideAppInitializer(() => {
      inject(TelemetryService);
    }),

    // Services
    AuthenticatorService,
    TenantService,
    MessageService,
    // Standalone pipes & directives
    PhoneNumberPipe,
    BooleanYesNoPipe,
    FormQuestionTypePipe,
    SupplierStatusTagSeverityPipe,
    PermissionsDirective,
    DatePipe,
  ],
};
